import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, PackageCheck, Link2, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TONE = { in_stock: "bg-emerald-50 text-emerald-700", incoming: "bg-sky-50 text-sky-700", arrival_overdue: "bg-amber-50 text-amber-800",
  not_ordered: "bg-slate-100 text-slate-600", status_unclear: "bg-rose-50 text-rose-700", on_order_no_date: "bg-rose-50 text-rose-700" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");
const stop = (e) => e.stopPropagation();

export function StatusChip({ u }) {
  return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${TONE[u.status] || ""}`} data-testid={`spa-unit-status-${u.unit_id}`}>{u.status_label}</span>;
}

export function DisplayChip({ u }) {
  if (!u.display_label) return <span className="text-slate-300">-</span>;
  const wet = u.display === "wet_demo";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${wet ? "bg-sky-50 text-sky-700" : "bg-violet-50 text-violet-700"}`} data-testid={`spa-unit-display-${u.unit_id}`}>{u.display_label}</span>;
}

export function ArrivalCell({ u }) {
  if (u.received_at) return <span className="text-xs text-slate-600" data-testid={`spa-unit-received-${u.unit_id}`}>Received {fmt(u.received_at)}{u.original_eta && <span className="block text-slate-400">expected {fmt(u.original_eta)}</span>}</span>;
  if (!u.eta) return <span className="text-slate-300">-</span>;
  return <span className="text-slate-700">{fmt(u.eta)}{u.eta_provisional && <span className="ml-1 text-xs font-semibold text-amber-700">TBC</span>}</span>;
}

export function ReservedCell({ u }) {
  if (u.holder?.startsWith("reserved")) return <span className="text-slate-800">{u.customer_note}{(u.postcode || u.month_required) && <span className="block text-xs text-slate-500">{[u.postcode, u.month_required].filter(Boolean).join(" · ")}</span>}</span>;
  return u.holder === "showroom" ? <span className="text-slate-500">Showroom</span> : <span className="text-emerald-700 font-medium">Free</span>;
}

export function FlagsCell({ u }) {
  if (!u.flags?.length) return <span className="text-slate-300">-</span>;
  return <span className="inline-flex items-start gap-1 text-xs text-amber-700 whitespace-normal" data-testid={`spa-unit-flags-${u.unit_id}`}><AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />{u.flags.join(" · ")}</span>;
}

export function SerialCell({ u, canManage, act }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(u.serial || "");
  if (!edit && u.serial) {
    return <span className="inline-flex items-center gap-1.5 font-mono text-xs text-slate-700">{u.serial}
      {canManage && <button type="button" onClick={(e) => { stop(e); setEdit(true); }} className="text-slate-400 hover:text-slate-700" aria-label="Correct serial" data-testid={`spa-serial-edit-${u.unit_id}`}><Pencil className="w-3 h-3" /></button>}</span>;
  }
  if (!canManage) return <span className="text-amber-700 text-xs font-medium">Not recorded</span>;
  return (
    <form className="flex gap-1" onClick={stop} onSubmit={(e) => { e.preventDefault(); if (v.trim()) act(() => api.put(`/spa-units/${u.unit_id}/serial`, { serial: v }), "Serial number saved"); }}>
      <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="Add serial" className="h-8 w-28 font-mono text-xs" data-testid={`spa-serial-input-${u.unit_id}`} />
      <Button size="sm" type="submit" variant="outline" className="h-8" disabled={!v.trim()} data-testid={`spa-serial-save-${u.unit_id}`}>Save</Button>
    </form>);
}

export function JobCell({ u, canManage, act }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(u.job_legacy_id || "");
  if (edit) {
    return (
      <form className="flex gap-1" onClick={stop} onSubmit={(e) => { e.preventDefault(); act(() => api.put(`/spa-units/${u.unit_id}/job`, { job_legacy_id: v || null }), v ? `Linked to job ${v}` : "Job link removed"); }}>
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder="Job no." className="h-8 w-24" data-testid={`spa-job-input-${u.unit_id}`} />
        <Button size="sm" type="submit" className="h-8" data-testid={`spa-job-save-${u.unit_id}`}>Save</Button>
      </form>);
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {u.job_legacy_id ? <Link to={`/tasks/${u.job_legacy_id}`} onClick={stop} className="text-indigo-700 hover:underline" data-testid={`spa-job-link-${u.unit_id}`}>#{u.job_legacy_id}<span className="block text-xs text-slate-500 max-w-[160px] truncate">{u.job_title}</span></Link> : <span className="text-slate-300">-</span>}
      {canManage && <button type="button" onClick={(e) => { stop(e); setEdit(true); }} className="text-slate-400 hover:text-slate-700" aria-label="Link job" data-testid={`spa-job-edit-${u.unit_id}`}><Link2 className="w-3.5 h-3.5" /></button>}
    </span>);
}

export function ReceiveCell({ u, canManage, act }) {
  const [dest, setDest] = useState("warehouse");
  if (!canManage || !["incoming", "arrival_overdue", "on_order_no_date"].includes(u.status)) return null;
  return (
    <span className="inline-flex gap-1" onClick={stop}>
      <select value={dest} onChange={(e) => setDest(e.target.value)} className="h-8 rounded-md border border-slate-200 text-xs" aria-label="Receive into" data-testid={`spa-receive-dest-${u.unit_id}`}>
        <option value="warehouse">Warehouse</option><option value="showroom">Showroom</option></select>
      <Button size="sm" variant="outline" className="h-8 whitespace-nowrap" data-testid={`spa-receive-${u.unit_id}`}
        onClick={() => window.confirm(`Confirm ${u.title}${u.serial ? ` (${u.serial})` : ""} has physically arrived at the ${dest}?`) && act(() => api.post(`/spa-units/${u.unit_id}/confirm-arrival`, { location: dest }), "Arrival confirmed - stock updated")}>
        <PackageCheck className="w-4 h-4 mr-1" />Confirm arrival</Button>
    </span>);
}
